import { ChangeDetectionStrategy, Component, computed, input, output, viewChild } from '@angular/core';

import { SheetFormComponent } from '@modules/sheets/components/sheet-form/sheet-form.component';
import { SheetsEditorComponent } from '@modules/sheets/components/sheets-editor/sheets-editor.component';
import { EditorChrome } from '@modules/sheets/editor-chrome';
import { EditorDownloads } from '@modules/sheets/editor-downloads';
import { EditorSaveStatus } from '@modules/sheets/editor-save-status';
import { Sheet } from '@modules/sheets/types/sheet';

/**
 * SBUX-1043: a workbook open in the fourth panel, grid and all.
 *
 * The same arrangement the Sheets page uses for an expanded workbook (`SheetsExpandedViewComponent`,
 * SBUX-1036): the workbook's own form is the header — title, save state, Download and share, the item
 * menu, the dates and the tabs — and the editor is projected in as its Sheets tab. The difference is
 * only where it is told what to open: the page's editor reads the route, this one is handed a
 * workbook id, which is what keeps it from driving the section (see `SheetsEditorComponent.hosted`).
 *
 * **The three editor services are provided HERE, not taken from the root.** `EditorSaveStatus`,
 * `EditorChrome` and `EditorDownloads` are all last-attach-wins singletons that carry exactly one
 * editor's state between an editor and the header above it. With one editor on the page that is fine;
 * with the panel's editor beside the page's, the second one to mount would take them over — the main
 * header would show the panel's save state, and "Download and share" picked in the main header would
 * run in the panel. A provider here gives this subtree its own three, so each editor talks to the
 * header directly above it.
 */
@Component({
  selector: 'app-sheets-panel-workbook',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SheetFormComponent, SheetsEditorComponent],
  providers: [EditorSaveStatus, EditorChrome, EditorDownloads],
  templateUrl: './sheets-panel-workbook.component.html',
  styleUrls: ['./sheets-panel-workbook.component.less'],
})
export class SheetsPanelWorkbookComponent {
  public readonly workbook = input.required<Sheet>();

  /**
   * A TEMPLATE is not opened as a workbook anywhere in Sheets: its detail view offers
   * **"+ Use template"** where a workbook's offers "Edit workbook" (Wook, 2026-10-06), because using
   * one makes a new workbook rather than editing the template in place. So the panel shows a template
   * the way the section does — the detail view with its preview — and starts no engine for it, which
   * is also what the handoff asks ("templates use a read-only preview instead of mounting an editable
   * panel engine").
   */
  protected readonly isTemplate = computed(() => !!this.workbook().template);

  /** Back, and the header's own X: both return to the panel's list. The panel runs the guards. */
  public readonly closed = output<void>();

  /**
   * The tab to open on, when the panel is coming back to a workbook it already had open (SBUX-1043).
   * Null is "whatever the form opens on", which is its first tab.
   */
  public readonly tab = input<string | null>(null);

  /** Which tab is showing now, so the panel can remember it for the next time it is built. */
  public readonly tabChanged = output<string>();

  /** The worksheet to open on, and the one showing now — remembered by the panel for the same reason. */
  public readonly sheetId = input<string | null>(null);
  public readonly activeSheetChanged = output<string>();

  /** The cell to put the user back on, once that worksheet is open. */
  public readonly cell = input<string | null>(null);

  /** Where they are now, as it changes — the panel keeps the last one and stores it as it goes. */
  public readonly activeCellChanged = output<string>();

  /** A workbook the form creates — "+ Use template" — opens in the panel, not in the main app. */
  public readonly created = output<Sheet>();

  /**
   * The editor's identity: the one workbook id it is to open, or nothing for a template. The template
   * iterates it so that a NEW id builds a new editor — see the comment there for what sharing one
   * across two workbooks did.
   */
  protected readonly editorKey = computed(() => (this.isTemplate() ? [] : [this.workbook().id]));

  /** Bound as a value, so the form holds one stable reference rather than a new closure per render. */
  protected readonly openInPanel = (sheet: Sheet): void => this.created.emit(sheet);

  /** "Open in main app" — once the grid has been flushed. The panel does the routing. */
  public readonly openedInMain = output<void>();

  /**
   * The prototype flushes the engine before handing the workbook to the main app
   * (`navigate()` in `fourth-panel-sheets.tsx`: a cell draft is offered for discard, otherwise
   * `engine.flush()` runs and the action proceeds). `canLeave` is that flush here — it commits the
   * cell being typed and refuses only work it cannot save — so the main editor never opens on a
   * revision older than what is on screen beside it.
   */
  protected async openInMain(): Promise<void> {
    if (!(await this.canLeave())) return;
    this.openedInMain.emit();
  }

  private readonly editor = viewChild(SheetsEditorComponent);

  /**
   * Whether the panel may close this workbook: the editor keeps the cell being typed in, and says
   * no only for work it cannot save. On the Sheets page the route guard asks the same question; the
   * panel has no route to leave, so it asks here.
   */
  public canLeave(): Promise<boolean> {
    // A template has no editor to ask; nothing is being typed into a grid that is not there.
    return this.editor()?.canLeave() ?? Promise.resolve(true);
  }
}
